import Vue from 'vue';
import Vuex from 'vuex';
import { Notification } from 'element-ui';
import { login, getMenu, getPermission } from '@/api/public.js';
import { getMessageUnread, messageStreamUrl } from '@/api/mine.js';

Vue.use(Vuex);

// 从菜单列表中提取权限
function extractPermissionsFromMenu(menuList) {
  const permissions = [];
  
  function traverseMenu(menu) {
    // 添加当前菜单的权限
    permissions.push({
      name: menu.name,
      label: menu.name,
      path: menu.path
    });
    
    // 递归处理子菜单
    if (menu.children && menu.children.length > 0) {
      menu.children.forEach(subMenu => traverseMenu(subMenu));
    }
  }
  
  menuList.forEach(menu => traverseMenu(menu));
  return permissions;
}

// 后端菜单树（path/name/icon/component/children）→ 前端侧边栏菜单结构
function buildMenuList(tree) {
  return (tree || []).map(node => ({
    id: node.id,
    path: node.path || '',
    name: node.name,
    icon: node.icon || '',
    component: node.component || '',
    isParent: node.children && node.children.length > 0,
    children: buildMenuList(node.children || [])
  }));
}

// 创建Vuex store
export default new Vuex.Store({
  // 状态
  state: {
    // 用户信息
    userInfo: {
      name: '',
      avatar: '',
      gender: '',
      phone: '',
      company_name: '',
      role: '', // 用户角色
      permissions: [] // 用户权限列表
    },
    // 是否登录
    isLogin: false,
    // 权限列表
    permissionList: [],
    // 菜单列表
    menuList: [],
    // 未读消息数（顶栏角标，我的通知 / 我的消息）
    unreadCount: {
      notice: 0,
      message: 0
    },
    // 实时通道（SSE）连接实例：只在前端内存里持有，不持久化
    sseConn: null,
    // 最近一次收到「新公告」推送的时间戳：消息页 watch 它来重拉列表
    noticeEventAt: 0
  },
  
  // 改变状态的方法
  mutations: {
    // 设置用户信息
    setUserInfo(state, userInfo) {
      state.userInfo = userInfo;
      state.isLogin = true;
      // 保存用户信息到localStorage
      localStorage.setItem('userAccount', JSON.stringify(userInfo));
    },
    
    // 清除用户信息
    clearUserInfo(state) {
      state.userInfo = {
        name: '',
        avatar: '',
        gender: '',
        phone: '',
        company_name: '',
        role: '',
        permissions: []
      };
      state.isLogin = false;
      // 清除未读角标
      state.unreadCount = { notice: 0, message: 0 };
      // 清除localStorage中的用户信息
      localStorage.removeItem('userAccount');
    },
    
    // 更新用户头像
    updateUserAvatar(state, avatar) {
      state.userInfo.avatar = avatar;
      // 更新localStorage中的用户信息
      localStorage.setItem('userAccount', JSON.stringify(state.userInfo));
    },
    
    // 更新用户信息（头像和昵称）
    updateUserInfo(state, userInfo) {
      state.userInfo = { ...state.userInfo, ...userInfo };
      // 更新localStorage中的用户信息
      localStorage.setItem('userAccount', JSON.stringify(state.userInfo));
    },
    
    // 设置权限列表
    setPermissionList(state, permissions) {
      state.permissionList = permissions;
    },
    
    // 设置用户权限
    setUserPermissions(state, permissions) {
      state.userInfo.permissions = permissions;
      // 更新localStorage中的用户信息
      localStorage.setItem('userAccount', JSON.stringify(state.userInfo));
    },
    
    // 设置菜单列表
    setMenuList(state, menuList) {
      state.menuList = menuList;
    },

    // 设置未读角标（我的通知 / 我的消息）
    setUnreadCount(state, counts) {
      const data = counts || {};
      state.unreadCount = {
        notice: Number(data.notice) || 0,
        message: Number(data.message) || 0
      };
    },

    // 实时通道连接实例
    setSseConn(state, conn) {
      state.sseConn = conn;
    },

    // 收到新公告推送（只记时间戳，数据仍由页面调接口取）
    setNoticeEventAt(state, at) {
      state.noticeEventAt = at;
    }
  },
  
  // 异步操作
  actions: {
    // 登录（始终走真实后端，不受 USE_MOCK 影响）
    // 后端登录接口只返回 { token, user }，菜单与权限需登录后另行获取
    login({ commit, dispatch }, loginData) {
      return login(loginData).then(response => {
        if (response.code === 200) {
          const { token, user, company } = response.data;

          // 存 token：后续 /admin/menu、/admin/permission 请求自动带 Authorization
          localStorage.setItem('token', token);

          // 并行拉取菜单与权限
          return Promise.all([getMenu(), getPermission()]).then(([menuRes, permRes]) => {
            const menuTree = menuRes.code === 200 ? (menuRes.data || []) : [];
            const permData = permRes.code === 200
              ? (permRes.data || { roles: [], permissions: [] })
              : { roles: [], permissions: [] };

            // 菜单树 → 前端侧边栏菜单
            const menuList = buildMenuList(menuTree);
            commit('setMenuList', menuList);
            localStorage.setItem('menuList', JSON.stringify(menuList));

            // 用户信息（avatar/gender 来自 tb_sys_admin，company 来自 tb_sys_company）
            const userInfo = {
              name: user.username || '管理员',
              avatar: user.avatar || '',
              gender: (user.gender === 0 || user.gender === 1) ? user.gender : '',
              phone: '',
              // 关联人员 id（tb_sys_admin.person_id → tb_sys_person.id）：0 = 未关联
              // 顶栏「推广二维码」用它取该人员的推广码，扫码日志的 share_user_id 就是这个 id
              person_id: (user.person_id === undefined || user.person_id === null) ? 0 : user.person_id,
              company_id: (user.company_id === undefined || user.company_id === null) ? 0 : user.company_id,
              company_name: (company && company.name) ? company.name : '',
              is_super: user.is_super,
              role: user.is_super === 1 ? '超级管理员' : (permData.roles[0] || '管理员'),
              permissions: permData.permissions || []
            };
            commit('setUserInfo', userInfo);
            commit('setUserPermissions', userInfo.permissions);
            localStorage.setItem('permissionList', JSON.stringify(permData.permissions));

            // 登录完成即建立实时通道（token 已落 localStorage）
            dispatch('connectMessageStream');

            return response;
          });
        } else {
          // 登录失败，抛出错误
          throw new Error(response.msg || '登录失败');
        }
      });
    },
    
    /**
     * 建立实时通道（SSE 长连接）
     *
     * 通道只负责「有新公告」这一件事：收到后刷新未读角标 + 记时间戳（消息页据此重拉列表），
     * 正文数据仍由页面调接口取，保证推送与列表口径永远一致。
     * 断线由浏览器自动重连（服务端每 25 秒发心跳保活，连接活满 30 分钟由服务端收尾、浏览器自动重连）；
     * token 失效时后端返回的不是 event-stream，浏览器会关闭且不再重连，此处不处理——下次操自然会跳登录。
     */
    connectMessageStream({ state, commit, dispatch }) {
      // 已有连接 / 未登录 / 浏览器不支持（老 IE）：静默不连，页面手动刷新仍可用
      if (state.sseConn || !localStorage.getItem('token')) {
        return;
      }
      if (typeof EventSource === 'undefined') {
        return;
      }

      const es = new EventSource(messageStreamUrl());

      // 通道就绪：拉一次未读数，保证进页面角标就是新的
      es.addEventListener('connected', () => {
        dispatch('loadUnreadCount');
      });

      // 收到公告事件：刷角标 + 通知消息页刷新；发布再弹桌面右下角提示，删除只刷不弹
      es.addEventListener('admin_notice', (event) => {
        let payload = {};
        try {
          payload = JSON.parse(event.data || '{}');
        } catch (e) {
          payload = {};
        }
        dispatch('loadUnreadCount');
        commit('setNoticeEventAt', Date.now());
        if (payload.action === 'deleted') {
          return;
        }
        Notification({
          title: '新的系统公告',
          message: payload.title || '收到一条新的系统公告',
          type: 'info',
          duration: 6000
        });
      });

      commit('setSseConn', es);
    },

    // 关闭实时通道（退出登录时调用）
    closeMessageStream({ state, commit }) {
      if (state.sseConn) {
        state.sseConn.close();
        commit('setSseConn', null);
      }
    },

    // 刷新未读角标（我的通知 / 我的消息）；未登录不请求
    loadUnreadCount({ commit }) {
      if (!localStorage.getItem('token')) {
        return Promise.resolve();
      }
      return getMessageUnread().then(res => {
        if (res.code === 200) {
          commit('setUnreadCount', res.data || {});
        }
        return res;
      }).catch(() => {});
    },

    // 退出登录
    logout({ commit, dispatch }) {
      // 先断开实时通道，避免退出后还挂着连接收推送
      dispatch('closeMessageStream');
      // 清除用户信息
      commit('clearUserInfo');
      // 清除菜单列表
      commit('setMenuList', []);
      // 清除token
      localStorage.removeItem('token');
      // 清除localStorage中的菜单和权限
      localStorage.removeItem('menuList');
      localStorage.removeItem('permissionList');
    },
    
    // 初始化用户信息
    initUserInfo({ commit }) {
      // 从localStorage中获取用户信息
      const userAccount = localStorage.getItem('userAccount');
      if (userAccount) {
        try {
          const userInfo = JSON.parse(userAccount);
          commit('setUserInfo', userInfo);
        } catch (error) {
          console.error('Failed to parse userAccount from localStorage:', error);
          localStorage.removeItem('userAccount');
        }
      }
      
      // 从localStorage中获取菜单列表
      const menuListStr = localStorage.getItem('menuList');
      if (menuListStr) {
        try {
          const menuList = JSON.parse(menuListStr);
          commit('setMenuList', menuList);
        } catch (error) {
          console.error('Failed to parse menuList from localStorage:', error);
          localStorage.removeItem('menuList');
        }
      }
      
      // 从localStorage中获取权限列表
      const permissionListStr = localStorage.getItem('permissionList');
      if (permissionListStr) {
        try {
          const permissionList = JSON.parse(permissionListStr);
          commit('setPermissionList', permissionList);
        } catch (error) {
          console.error('Failed to parse permissionList from localStorage:', error);
          localStorage.removeItem('permissionList');
        }
      }
    },
    
    // 加载权限列表
    loadPermissionList({ commit, state }) {
      // 如果用户已登录且有菜单列表，直接使用菜单列表生成权限
      if (state.isLogin && state.menuList.length > 0) {
        // 从菜单列表中提取权限
        const permissions = extractPermissionsFromMenu(state.menuList);
        commit('setPermissionList', permissions);
        return Promise.resolve(permissions);
      }
      
      // 模拟权限列表（家脉），实际项目中应该从API获取
      // 与 src/mock/menu.js 的 MENU_TREE 保持一致
      const mockPermissionList = [
        { name: 'home', label: '主页', path: '/home' },
        { name: 'company', label: '公司管理', path: '/system/company' },
        { name: 'dept', label: '部门管理', path: '/system/dept' },
        { name: 'person', label: '人员管理', path: '/system/person' },
        { name: 'admin', label: '账号管理', path: '/system/admin' },
        { name: 'role', label: '角色管理', path: '/system/role' },
        { name: 'menu', label: '菜单管理', path: '/system/menu' },
        { name: 'config', label: '系统参数', path: '/system/config' },
        { name: 'dict_activity', label: '活动类别', path: '/dict/activity_category' },
        { name: 'dict_content', label: '内容分类', path: '/dict/content_category' },
        { name: 'dict_banner', label: '轮播图位置', path: '/dict/banner_position' },
        { name: 'dict_tower', label: '塔林介绍板块', path: '/dict/tower_section' },
        { name: 'dict_benefit', label: '券类型', path: '/dict/benefit_category' },
        { name: 'dict_milestone', label: '发放条件', path: '/dict/dispatch_milestone' },
        { name: 'dict_scenic', label: '景区等级', path: '/dict/scenic_level' },
        { name: 'dict_facility', label: '配套设施类别', path: '/dict/facility_category' },
        { name: 'activity_list', label: '活动列表', path: '/activity/list' },
        { name: 'log_operation', label: '操作日志', path: '/log/operation' },
        { name: 'log_login', label: '登录日志', path: '/log/login' },
        { name: 'content_banner', label: '轮播图', path: '/content/banner' },
        { name: 'content_official', label: '官方内容', path: '/content/official' },
        { name: 'content_post', label: '社区帖子', path: '/content/post' },
        { name: 'content_launch', label: '落地页', path: '/content/launch' },
        { name: 'content_tower', label: '塔林介绍', path: '/content/tower' },
        { name: 'stay_base', label: '旅居基地', path: '/stay/base' },
        { name: 'stay_scenic', label: '周边景区', path: '/stay/scenic' },
        { name: 'stay_community', label: '小区管理', path: '/stay/community' },
        { name: 'user_list', label: '用户列表', path: '/user/list' },
        { name: 'user_auth', label: '实名认证', path: '/user/auth' },
        { name: 'benefit_checkin', label: '券码核销', path: '/benefit/checkin' },
        { name: 'benefit_list', label: '权益券', path: '/benefit/list' },
        { name: 'benefit_user', label: '持券记录', path: '/benefit/user' },
        { name: 'benefit_verify', label: '核销记录', path: '/benefit/verify' },
        { name: 'order_appointment', label: '预约列表', path: '/order/appointment' }
      ];
      
      commit('setPermissionList', mockPermissionList);
      return Promise.resolve(mockPermissionList);
    }
  },
  
  // 计算属性
  getters: {
    // 获取用户信息
    getUserInfo: state => state.userInfo,
    // 获取是否登录
    getIsLogin: state => state.isLogin,
    // 获取用户名
    getUserName: state => state.userInfo.name,
    // 获取用户头像
    getUserAvatar: state => state.userInfo.avatar,
    // 获取所属公司名
    getCompanyName: state => state.userInfo.company_name || '',
    // 获取用户性别（0男 1女，''未知）
    getUserGender: state => state.userInfo.gender,
    // 获取用户角色
    getUserRole: state => state.userInfo.role,
    // 获取用户权限列表
    getUserPermissions: state => state.userInfo.permissions,
    // 获取权限列表
    getPermissionList: state => state.permissionList,
    // 获取未读角标数（我的通知 / 我的消息）
    getUnreadCount: state => state.unreadCount,
    // 最近一次新公告推送的时间戳（消息页 watch 它刷新列表）
    getNoticeEventAt: state => state.noticeEventAt,
    // 检查用户是否有某个权限
    hasPermission: state => permission => {
      return state.userInfo.permissions.includes(permission);
    },
    // 检查用户是否有多个权限中的任意一个
    hasAnyPermission: state => permissions => {
      return permissions.some(permission => state.userInfo.permissions.includes(permission));
    },
    // 检查用户是否有全部权限
    hasAllPermissions: state => permissions => {
      return permissions.every(permission => state.userInfo.permissions.includes(permission));
    }
  }
});
